import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { errCode } from "@/lib/api";
import { WhatsAppIcon, whatsappUrl } from "@/components/ContactWhatsApp";

const Field = ({ id, label, hint, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-slate-300">{label}</Label>
    <Input id={id} className="h-12 rounded-xl border-white/10 bg-white/5 text-base" {...props} />
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const PasswordField = ({ id, label, hint, error, "data-testid": testId, ...props }) => {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-slate-300">{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} className={`h-12 rounded-xl bg-white/5 pr-12 text-base ${error ? "border-red-400/70" : "border-white/10"}`} data-testid={testId} {...props} />
        <button type="button" onClick={() => setShow(!show)} aria-label={show ? t("auth.hidePassword") : t("auth.showPassword")}
          className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:text-amber-300"
          data-testid={`${testId}-toggle`}>
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {error ? <p className="text-xs text-red-300" data-testid={`${testId}-error`}>{error}</p> : hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

const SubmitBtn = ({ busy, children, testId }) => (
  <button
    type="submit"
    disabled={busy}
    data-testid={testId}
    className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display text-base font-bold text-[#0B0D12] transition-transform duration-150 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60"
  >
    {busy && <Loader2 className="h-5 w-5 animate-spin" />}
    {children}
  </button>
);

const LoginForm = ({ onDone }) => {
  const { t, tErr } = useI18n();
  const { login } = useAuth();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f);
      toast.success(`${t("auth.welcome")} ${u.pseudo}`);
      onDone();
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <Field id="identifier" label={t("auth.identifier")} value={f.identifier} required autoComplete="username"
        onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="login-identifier-input" />
      <PasswordField id="password" label={t("auth.password")} value={f.password} required autoComplete="current-password"
        onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
      <SubmitBtn busy={busy} testId="login-submit-btn">{t("auth.submitLogin")}</SubmitBtn>
      <ForgotPassword identifier={f.identifier} />
    </form>
  );
};

const ForgotPassword = ({ identifier }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="text-center">
      <button type="button" onClick={() => setOpen(!open)} className="text-sm text-amber-300 underline-offset-4 hover:underline" data-testid="forgot-password-btn">
        {t("auth.forgot")}
      </button>
      {open && (
        <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left text-sm text-slate-300" data-testid="forgot-password-box">
          <p>{t("auth.forgotText")}</p>
          <a href={whatsappUrl(`${t("auth.forgotMsg")} ${identifier || ""}`.trim())} target="_blank" rel="noopener noreferrer"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] font-bold text-white hover:opacity-90" data-testid="forgot-password-whatsapp">
            <WhatsAppIcon className="h-5 w-5" /> {t("auth.forgotCta")}
          </a>
        </div>
      )}
    </div>
  );
};

const ContactSwitch = ({ via, setVia }) => {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-2 rounded-full border border-white/10 bg-white/5 p-1">
      {["email", "phone"].map((v) => (
        <button key={v} type="button" onClick={() => setVia(v)} data-testid={`signup-via-${v}`}
          className={`h-10 rounded-full text-sm font-semibold transition-colors ${via === v ? "bg-white/10 text-white" : "text-slate-400"}`}>
          {t(v === "email" ? "auth.useEmail" : "auth.usePhone")}
        </button>
      ))}
    </div>
  );
};

const SignupForm = ({ onDone }) => {
  const { t, tErr, lang } = useI18n();
  const { register } = useAuth();
  const [via, setVia] = useState("phone");
  const [f, setF] = useState({ pseudo: "", email: "", phone: "", password: "", password2: "", birth_date: "", ref: localStorage.getItem("bkm_ref") || "" });
  const mismatch = f.password2 !== "" && f.password2 !== f.password;
  const [adult, setAdult] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password2) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      const body = { pseudo: f.pseudo, password: f.password, birth_date: f.birth_date, confirm_adult: adult, accept_terms: terms, language: lang };
      if (via === "email") body.email = f.email;
      else body.phone = f.phone;
      const ref = f.ref.trim().toUpperCase();
      if (ref) body.ref = ref;
      const u = await register(body);
      localStorage.removeItem("bkm_ref");
      toast.success(`${t("auth.welcome")} ${u.pseudo}`);
      onDone();
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
      <Field id="pseudo" label={t("auth.pseudo")} hint={t("auth.pseudoHint")} value={f.pseudo} required onChange={set("pseudo")} data-testid="signup-pseudo-input" />
      <ContactSwitch via={via} setVia={setVia} />
      {via === "email" ? (
        <Field id="email" type="email" label={t("auth.email")} value={f.email} required onChange={set("email")} data-testid="signup-email-input" />
      ) : (
        <Field id="phone" type="tel" inputMode="tel" placeholder="6XX XX XX XX" label={t("auth.phone")} value={f.phone} required onChange={set("phone")} data-testid="signup-phone-input" />
      )}
      <PasswordField id="signup-password" label={t("auth.password")} hint={t("auth.passwordHint")} value={f.password} required minLength={8} autoComplete="new-password" onChange={set("password")} data-testid="signup-password-input" />
      <PasswordField id="signup-password2" label={t("auth.confirmPassword")} value={f.password2} required minLength={8} autoComplete="new-password" onChange={set("password2")}
        error={mismatch ? t("auth.passwordMismatch") : null} data-testid="signup-password-confirm-input" />
      <Field id="birth" type="date" label={t("auth.birthDate")} value={f.birth_date} required onChange={set("birth_date")} data-testid="signup-birthdate-input" />
      <Field id="ref" label={t("auth.refCode")} hint={t("auth.refHint")} value={f.ref} maxLength={12} autoCapitalize="characters" onChange={set("ref")} data-testid="signup-ref-input" />
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm text-slate-300">
        <Checkbox checked={adult} onCheckedChange={(v) => setAdult(!!v)} className="mt-0.5 h-5 w-5" data-testid="signup-adult-checkbox" />
        {t("auth.confirmAdult")}
      </label>
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm text-slate-300">
        <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5 h-5 w-5" data-testid="signup-terms-checkbox" />
        <span>
          {t("auth.acceptTerms")} (<Link to="/legal/terms" className="text-amber-400 underline">{t("legal.terms")}</Link>)
        </span>
      </label>
      <SubmitBtn busy={busy} testId="signup-submit-btn">{t("auth.submitSignup")}</SubmitBtn>
    </form>
  );
};

export default function AuthPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = params.get("mode") === "signup" ? "signup" : "login";
  const done = () => navigate(location.state?.from || "/wallet", { replace: true });
  if (user) return <Navigate to={location.state?.from || "/wallet"} replace />;
  const isLogin = mode === "login";
  return (
    <div className="mx-auto max-w-md py-4 rise" data-testid="auth-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t(isLogin ? "auth.loginTitle" : "auth.signupTitle")}</h1>
      <p className="mt-2 text-sm text-slate-400">{t(isLogin ? "auth.loginSub" : "auth.signupSub")}</p>
      <div className="surface mt-8 p-5 sm:p-7">
        {isLogin ? <LoginForm onDone={done} /> : <SignupForm onDone={done} />}
      </div>
      <p className="mt-6 text-center text-sm text-slate-400">
        {t(isLogin ? "auth.noAccount" : "auth.haveAccount")}{" "}
        <button onClick={() => setParams(isLogin ? { mode: "signup" } : {})} className="min-h-[44px] font-semibold text-amber-400" data-testid="auth-switch-mode-btn">
          {t(isLogin ? "nav.signup" : "nav.login")}
        </button>
      </p>
    </div>
  );
}
